import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiLoader,
  FiAlertCircle,
  FiMessageCircle,
  FiEye,
  FiClock,
  FiChevronUp,
  FiChevronDown,
  FiUser,
  FiSend,
  FiHash,
  FiShare2,
  FiBookmark,
  FiFlag,
  FiAward,
  FiMoreHorizontal,
  FiExternalLink,
  FiRefreshCw,
  FiCheck,
  FiZoomIn,
} from "react-icons/fi";
import { toast } from "react-toastify";
import communityService from "../../../services/communityService";
import communityFeedService from "../../../services/communities/communityFeedService";
import { useSavedThreadIds, useSaveThread, useUnsaveThread } from "../../../services/communities";
import ReplyComponent from "../threads/ReplyComponent";
import ShareThreadButton from "../threads/ShareThreadButton";
import FollowThreadButton from "../threads/FollowThreadButton";
import ImageLightbox from "../threads/ImageLightbox";
import "./ThreadDetailSection.css";

// Lazy load modals
const ReportThreadModal = lazy(() => import("../threads/ReportThreadModal"));
const AwardThreadModal = lazy(() => import("../threads/AwardThreadModal"));

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

/**
 * ThreadDetailSection - Thread detail view in CommunitiesHub layout
 * State-of-the-art UI matching the feed design
 */
const ThreadDetailSection = () => {
  const { communityId: paramCommunityId, "*": splat } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract threadId and communityId from URL
  // URL pattern: /communities/:communityId/threads/:threadId
  // When using /* wildcard route, extract from pathname
  const { threadId, communityId } = useMemo(() => {
    const path = location.pathname;
    
    // Try to extract both from pathname
    const pathMatch = path.match(/\/communities\/([a-f0-9]{24})\/threads\/([a-f0-9]{24})/i);
    if (pathMatch) {
      return { communityId: pathMatch[1], threadId: pathMatch[2] };
    }
    
    // Fallback: try splat for threadId
    if (splat) {
      const splatMatch = splat.match(/^threads\/([a-f0-9]{24})$/i);
      if (splatMatch && paramCommunityId) {
        return { communityId: paramCommunityId, threadId: splatMatch[1] };
      }
    }
    
    return { communityId: paramCommunityId, threadId: null };
  }, [splat, location.pathname, paramCommunityId]);

  const [thread, setThread] = useState(null);
  const [community, setCommunity] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const [showActions, setShowActions] = useState(false);
  
  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [localSavedOverride, setLocalSavedOverride] = useState(null); // Local override for optimistic UI
  const [savingThread, setSavingThread] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);

  const replyInputRef = useRef(null);
  const actionsRef = useRef(null);

  // Fetch saved thread IDs
  const { data: savedData } = useSavedThreadIds();
  
  // Save/unsave mutations for proper cache invalidation
  const saveThreadMutation = useSaveThread();
  const unsaveThreadMutation = useUnsaveThread();
  
  // Compute isSaved: use local override if set, otherwise check local cache, then backend userHasSaved
  const isSaved = useMemo(() => {
    if (localSavedOverride !== null) return localSavedOverride;
    if (savedData?.savedSet && threadId) return savedData.savedSet.has(threadId);
    // Fallback to thread.userHasSaved from backend
    return thread?.userHasSaved ?? false;
  }, [savedData, threadId, localSavedOverride, thread?.userHasSaved]);

  // Fetch thread and replies
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const threadRes = await communityService.getThread(communityId, threadId, 1, 50);
        if (threadRes.success) {
          setThread(threadRes.data.thread);
          // Store community data from new response structure
          if (threadRes.data.community) {
            setCommunity(threadRes.data.community);
          }

          const repliesRes = await communityService.getThreadReplies(communityId, threadId, {
            page: 1,
            limit: 50,
            sortBy: "createdAt",
            sortOrder: "asc",
          });
          if (repliesRes.success) {
            setReplies(repliesRes.data.replies || []);
          }
        } else {
          throw new Error("Thread not found");
        }
      } catch (err) {
setError(err.message || "Failed to load thread");
      } finally {
        setLoading(false);
      }
    };

    if (communityId && threadId) {
      fetchData();
    }
  }, [communityId, threadId]);

  // Close actions dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setShowActions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Upvote handler
  const handleUpvoteThread = useCallback(async () => {
    if (!thread) return;
    try {
      const response = await communityFeedService.voteThread(communityId, threadId);
      if (response.success) {
        setThread((prev) => ({
          ...prev,
          score: response.data.score,
          upvoteCount: response.data.upvoteCount,
          downvoteCount: response.data.downvoteCount,
          userHasUpvoted: response.data.userHasUpvoted,
          userHasDownvoted: response.data.userHasDownvoted,
        }));
      }
    } catch (err) {
      toast.error("Failed to vote");
    }
  }, [thread, communityId, threadId]);

  // Downvote handler
  const handleDownvoteThread = useCallback(async () => {
    if (!thread) return;
    try {
      const response = await communityFeedService.downvoteThread(communityId, threadId);
      if (response.success) {
        setThread((prev) => ({
          ...prev,
          score: response.data.score,
          upvoteCount: response.data.upvoteCount,
          downvoteCount: response.data.downvoteCount,
          userHasUpvoted: response.data.userHasUpvoted,
          userHasDownvoted: response.data.userHasDownvoted,
        }));
      }
    } catch (err) {
      toast.error("Failed to vote");
    }
  }, [thread, communityId, threadId]);

  // Submit reply
  const handleSubmitReply = useCallback(async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !thread) return;

    try {
      setSubmittingReply(true);
      const response = await communityService.createReply(communityId, threadId, {
        content: replyText.trim(),
        parentReply: replyingTo?.replyId || null,
      });

      if (response.success) {
        const repliesRes = await communityService.getThreadReplies(communityId, threadId, {
          page: 1,
          limit: 50,
          sortBy: "createdAt",
          sortOrder: "asc",
        });
        if (repliesRes.success) {
          setReplies(repliesRes.data.replies || []);
        }
        setThread((prev) => ({ ...prev, replyCount: prev.replyCount + 1 }));
        setReplyText("");
        setReplyingTo(null);
        toast.success("Reply posted!");
      }
    } catch (err) {
      toast.error("Failed to post reply");
    } finally {
      setSubmittingReply(false);
    }
  }, [replyText, thread, communityId, threadId, replyingTo]);

  // Vote on reply
  const handleVoteReply = useCallback(async (replyId) => {
    try {
      const response = await communityService.voteReply(communityId, threadId, replyId);
      if (response.success) {
        const updateVotes = (items) =>
          items.map((r) => {
            if (r._id === replyId) {
              return {
                ...r,
                upvoteCount: response.data.upvoteCount,
                userHasUpvoted: response.data.userHasUpvoted,
              };
            }
            if (r.children?.length) {
              return { ...r, children: updateVotes(r.children) };
            }
            return r;
          });
        setReplies(updateVotes);
      }
    } catch (err) {
      toast.error("Failed to vote");
    }
  }, [communityId, threadId]);

  const handleReplyToReply = useCallback((reply) => {
    setReplyingTo({ replyId: reply._id, authorName: reply.author.name });
    replyInputRef.current?.focus();
  }, []);

  const toggleReplyExpansion = useCallback((replyId) => {
    setExpandedReplies((prev) => {
      const newSet = new Set(prev);
      newSet.has(replyId) ? newSet.delete(replyId) : newSet.add(replyId);
      return newSet;
    });
  }, []);

  // Save thread
  const handleSaveThread = useCallback(async () => {
    if (savingThread || saveThreadMutation.isPending || unsaveThreadMutation.isPending) return;
    
    const newSavedState = !isSaved;
    setLocalSavedOverride(newSavedState); // Optimistic update
    setSavingThread(true);
    
    try {
      if (newSavedState) {
        await saveThreadMutation.mutateAsync({ threadId, collection: null, tags: [] });
      } else {
        await unsaveThreadMutation.mutateAsync({ threadId });
      }
      // Cache will be invalidated by the mutation, clear local override
      setTimeout(() => setLocalSavedOverride(null), 300);
    } catch (err) {
      setLocalSavedOverride(null); // Revert on error
      // Toast is handled by the mutation
    } finally {
      setSavingThread(false);
    }
  }, [threadId, isSaved, savingThread, saveThreadMutation, unsaveThreadMutation]);

  // Copy thread link
  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  }, []);

  // Format time ago
  const formatTimeAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const mins = Math.floor((now - date) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  }, []);

  const getProfileImage = useCallback((author) => 
    author?.signedProfilePhotoUrl || author?.profilePhotoUrl, []);

  const getScore = useMemo(() => {
    if (thread?.score !== undefined) return thread.score;
    return (thread?.upvoteCount || 0) - (thread?.downvoteCount || 0);
  }, [thread]);

  // Loading state
  if (loading) {
    return (
      <motion.div
        className="chtds-loading"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="chtds-loader">
          <FiLoader className="chtds-spin" size={36} />
        </div>
        <h3>Loading thread...</h3>
        <p>Fetching the discussion</p>
      </motion.div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div
        className="chtds-error"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="chtds-error-icon">
          <FiAlertCircle size={48} />
        </div>
        <h3>Thread not found</h3>
        <p>{error}</p>
        <button className="chtds-back-btn-large" onClick={() => navigate("/communities")}>
          <FiArrowLeft size={18} />
          Back to Communities
        </button>
      </motion.div>
    );
  }

  if (!thread) return null;

  return (
    <motion.div
      className="chtds-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Back Navigation */}
      <motion.div className="chtds-nav" variants={itemVariants}>
        <button className="chtds-back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={18} />
          <span>Back</span>
        </button>
        <div className="chtds-breadcrumb">
          <Link to="/communities" className="chtds-breadcrumb-link">Communities</Link>
          <span className="chtds-breadcrumb-sep">/</span>
          <Link 
            to={`/communities/${communityId}`} 
            className="chtds-breadcrumb-link"
          >
            {community?.name || thread.community?.name || "Community"}
          </Link>
          <span className="chtds-breadcrumb-sep">/</span>
          <span className="chtds-breadcrumb-current">Thread</span>
        </div>
      </motion.div>

      {/* Thread Card */}
      <motion.article className="chtds-thread-card" variants={itemVariants}>
        {/* Vote Section */}
        <div className="chtds-vote-section">
          <motion.button
            className={`chtds-vote-btn up ${thread.userHasUpvoted ? "active" : ""}`}
            onClick={handleUpvoteThread}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronUp size={24} strokeWidth={2.5} />
          </motion.button>
          
          <span className={`chtds-score ${getScore > 0 ? "positive" : getScore < 0 ? "negative" : ""}`}>
            {getScore}
          </span>
          
          <motion.button
            className={`chtds-vote-btn down ${thread.userHasDownvoted ? "active" : ""}`}
            onClick={handleDownvoteThread}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronDown size={24} strokeWidth={2.5} />
          </motion.button>
        </div>

        {/* Content */}
        <div className="chtds-content">
          {/* Header with community info */}
          <div className="chtds-header">
            <Link 
              to={`/communities/${communityId}/preview`}
              className="chtds-community-badge"
            >
              {(() => {
                // Use community from new API response (with photoUrl) or fallback to thread.community
                const communityData = community || thread.community;
                const photoUrl = communityData?.photoUrl || communityData?.signedPhotoURL || communityData?.photoURL;
                
                if (photoUrl) {
                  return (
                    <img 
                      src={photoUrl} 
                      alt={communityData?.name}
                      className="chtds-community-avatar"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  );
                }
                
                return (
                  <div className="chtds-community-avatar placeholder">
                    {communityData?.name?.charAt(0).toUpperCase() || "C"}
                  </div>
                );
              })()}
              <span className="chtds-community-name">{(community || thread.community)?.name}</span>
            </Link>
            
            <span className="chtds-separator">•</span>
            
            <div className="chtds-author">
              {getProfileImage(thread.author) ? (
                <img src={getProfileImage(thread.author)} alt={thread.author?.name} />
              ) : (
                <div className="chtds-author-avatar">
                  <FiUser size={10} />
                </div>
              )}
              <span>{thread.author?.name}</span>
            </div>
            
            <span className="chtds-time">
              <FiClock size={12} />
              {formatTimeAgo(thread.createdAt)}
              {thread.updatedAt !== thread.createdAt && " • edited"}
            </span>

            {thread.flair && (
              <span 
                className="chtds-flair"
                style={{ 
                  backgroundColor: typeof thread.flair === 'object' 
                    ? (thread.flair.backgroundColor || thread.flair.color || "#3b82f6")
                    : "#3b82f6",
                  color: "#fff"
                }}
              >
                {typeof thread.flair === 'object' ? thread.flair.text : thread.flair}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="chtds-title">{thread.title}</h1>

          {/* Description */}
          <div className="chtds-description">
            {thread.description}
          </div>

          {/* Single Photo (photoUrl field) - Click to expand */}
          {thread.photoUrl && (
            <motion.div 
              className="chtds-photos chtds-photo-clickable"
              onClick={() => {
                const url = thread.photoUrl.startsWith('http') 
                  ? thread.photoUrl 
                  : `https://ResearchPro.s3.ap-south-1.amazonaws.com${thread.photoUrl}`;
                setLightboxImageUrl(url);
                setShowImageLightbox(true);
              }}
              whileHover={{ scale: 1.005 }}
              whileTap={{ scale: 0.995 }}
            >
              <img
                src={thread.photoUrl.startsWith('http') 
                  ? thread.photoUrl 
                  : `https://ResearchPro.s3.ap-south-1.amazonaws.com${thread.photoUrl}`}
                alt={`${thread.title} - attached image`}
                className="chtds-photo"
                onError={(e) => {
e.target.style.display = 'none';
                }}
              />
              <div className="chtds-photo-overlay">
                <FiZoomIn size={24} />
                <span>Click to view full image</span>
              </div>
            </motion.div>
          )}

          {/* Multiple Photos (photos array field) */}
          {!thread.photoUrl && thread.photos?.length > 0 && (
            <div className="chtds-photos">
              {thread.photos.map((photo, idx) => {
                // Handle various photo URL field names
                const photoUrl = typeof photo === 'string' 
                  ? photo 
                  : photo.signedUrl || photo.url || photo.signedPhotoUrl || photo.photoUrl;
                
                if (!photoUrl) return null;
                
                return (
                  <motion.div
                    key={idx}
                    className="chtds-photo-clickable"
                    onClick={() => {
                      setLightboxImageUrl(photoUrl);
                      setShowImageLightbox(true);
                    }}
                    whileHover={{ scale: 1.005 }}
                    whileTap={{ scale: 0.995 }}
                  >
                    <img
                      src={photoUrl}
                      alt={`Thread photo ${idx + 1}`}
                      className="chtds-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="chtds-photo-overlay">
                      <FiZoomIn size={24} />
                      <span>Click to view full image</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Tags */}
          {thread.tags?.length > 0 && (
            <div className="chtds-tags">
              {thread.tags.map((tag, i) => (
                <Link 
                  key={i} 
                  to={`/communities?tag=${tag}`}
                  className="chtds-tag"
                >
                  <FiHash size={11} />
                  {tag}
                </Link>
              ))}
            </div>
          )}

          {/* Stats Row */}
          <div className="chtds-stats">
            <span className="chtds-stat">
              <FiMessageCircle size={14} />
              {thread.replyCount || 0} replies
            </span>
            <span className="chtds-stat">
              <FiEye size={14} />
              {thread.viewCount || 0} views
            </span>
          </div>

          {/* Actions Row */}
          <div className="chtds-actions">
            <ShareThreadButton 
              communityId={communityId}
              threadId={threadId}
              threadTitle={thread.title}
            />
            
            <motion.button 
              className={`chtds-action-btn ${isSaved ? "active" : ""}`}
              onClick={handleSaveThread}
              disabled={savingThread}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isSaved ? <FiCheck size={16} /> : <FiBookmark size={16} />}
              {savingThread ? "..." : isSaved ? "Saved" : "Save"}
            </motion.button>
            
            <motion.button 
              className="chtds-action-btn"
              onClick={() => setShowAwardModal(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiAward size={16} />
              Award
            </motion.button>

            <FollowThreadButton
              communityId={communityId}
              threadId={threadId}
              isFollowing={thread.isFollowing}
            />
            
            <div className="chtds-more-actions" ref={actionsRef}>
              <motion.button 
                className="chtds-action-btn"
                onClick={() => setShowActions(!showActions)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiMoreHorizontal size={16} />
              </motion.button>
              
              <AnimatePresence>
                {showActions && (
                  <motion.div
                    className="chtds-actions-dropdown"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                  >
                    <button onClick={() => { setShowReportModal(true); setShowActions(false); }}>
                      <FiFlag size={14} />
                      Report
                    </button>
                    <button onClick={() => window.open(window.location.href, '_blank')}>
                      <FiExternalLink size={14} />
                      Open in new tab
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.article>

      {/* Reply Form */}
      {thread.status !== "locked" && (
        <motion.div className="chtds-reply-form" variants={itemVariants}>
          <div className="chtds-reply-header">
            <FiMessageCircle size={18} />
            <h3>{replyingTo ? `Replying to ${replyingTo.authorName}` : "Add a Reply"}</h3>
            {replyingTo && (
              <button 
                className="chtds-cancel-reply"
                onClick={() => setReplyingTo(null)}
              >
                Cancel
              </button>
            )}
          </div>
          
          <form onSubmit={handleSubmitReply}>
            <textarea
              ref={replyInputRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="What are your thoughts? Be constructive and helpful..."
              rows={4}
              maxLength={2000}
              disabled={submittingReply}
              className="chtds-reply-input"
            />
            
            <div className="chtds-reply-footer">
              <span className="chtds-char-count">
                {replyText.length}/2000
              </span>
              <motion.button 
                type="submit" 
                className="chtds-submit-btn"
                disabled={!replyText.trim() || submittingReply}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSend size={16} />
                {submittingReply ? "Posting..." : "Post Reply"}
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Locked thread notice */}
      {thread.status === "locked" && (
        <motion.div className="chtds-locked-notice" variants={itemVariants}>
          <FiAlertCircle size={20} />
          <span>This thread has been locked. New replies are not allowed.</span>
        </motion.div>
      )}

      {/* Replies Section */}
      <motion.div className="chtds-replies-section" variants={itemVariants}>
        <div className="chtds-replies-header">
          <h3>
            <FiMessageCircle size={18} />
            Discussion ({thread.replyCount || 0})
          </h3>
          <button className="chtds-refresh-btn" onClick={() => window.location.reload()}>
            <FiRefreshCw size={14} />
            Refresh
          </button>
        </div>

        {replies.length === 0 ? (
          <div className="chtds-no-replies">
            <div className="chtds-no-replies-icon">
              <FiMessageCircle size={40} />
            </div>
            <h4>No replies yet</h4>
            <p>Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="chtds-replies-list">
            {replies.map((reply) => (
              <ReplyComponent
                key={reply._id}
                reply={reply}
                onVote={handleVoteReply}
                onReply={handleReplyToReply}
                formatTimeAgo={formatTimeAgo}
                expandedReplies={expandedReplies}
                onToggleExpansion={toggleReplyExpansion}
                communityId={communityId}
                threadId={threadId}
                depth={0}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Modals */}
      <Suspense fallback={null}>
        {showReportModal && (
          <ReportThreadModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            communityId={communityId}
            threadId={threadId}
            threadTitle={thread.title}
          />
        )}
        
        {showAwardModal && (
          <AwardThreadModal
            isOpen={showAwardModal}
            onClose={() => setShowAwardModal(false)}
            communityId={communityId}
            threadId={threadId}
            threadTitle={thread.title}
          />
        )}
      </Suspense>

      {/* Image Lightbox - Full screen photo viewer */}
      <ImageLightbox
        isOpen={showImageLightbox}
        imageUrl={lightboxImageUrl}
        alt={`${thread?.title} - attached image`}
        onClose={() => {
          setShowImageLightbox(false);
          setLightboxImageUrl(null);
        }}
        showDownload={true}
      />
    </motion.div>
  );
};

export default ThreadDetailSection;
